import React, { useState } from 'react';
import {
  X,
  Bot,
  Sparkles,
  Send,
  FileCheck2,
  ShieldCheck,
  CheckCircle2,
  BookOpen
} from 'lucide-react';

interface AIAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
}

export const AIAssistantModal: React.FC<AIAssistantModalProps> = ({
  isOpen,
  onClose,
  darkMode,
}) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [responseMarkdown, setResponseMarkdown] = useState<string | null>(null);

  if (!isOpen) return null;

  const samplePrompts = [
    'I need a biometric passport PSD template for an international travel agency mockup.',
    'Recommend an executive certificate layout with gold foil rosettes for a tech summit.',
    'Which utility bill and bank statement formats work best for corporate audit demonstrations?',
    'What software is needed to edit CR80 ID cards with smart layer photo frames?'
  ];

  const handleAskAI = async (p?: string) => {
    const query = p || prompt;
    if (!query.trim()) return;

    setLoading(true);
    setResponseMarkdown(null);

    try {
      const res = await fetch('/api/ai/recommend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: query }),
      });

      const data = await res.json();
      setLoading(false);

      if (data.success) {
        setResponseMarkdown(data.recommendation);
      } else {
        setResponseMarkdown('Failed to retrieve AI advice. Please select from our top categories: Passport, ID Card, Certificate, or Bank Statement.');
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      setResponseMarkdown('AI Advisor unavailable. Please verify connection.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-slate-950/95">
      
      <div className={`relative w-full max-w-2xl rounded-3xl border shadow-2xl overflow-hidden my-auto ${
        darkMode ? 'glass-card border-white/20 text-white' : 'glass-card-light border-slate-200 text-slate-900'
      }`}>
        
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-blue-950/40 via-purple-950/40 to-slate-950">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-500 flex items-center justify-center text-white shadow-lg">
              <Bot className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h3 className="font-black text-xl text-gradient">RH Editography AI Document Advisor</h3>
              <p className="text-xs text-slate-400">Powered by RH Intelligent AI Engine</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-900/95 hover:bg-rose-500 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto custom-scrollbar">
          
          {/* Preset Prompts */}
          <div className="space-y-2">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Quick AI Inquiries:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {samplePrompts.map((sp, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setPrompt(sp);
                    handleAskAI(sp);
                  }}
                  className="p-3 rounded-2xl bg-slate-900/50 hover:bg-slate-800/80 border border-white/10 hover:border-cyan-400 text-left text-xs font-semibold text-slate-300 hover:text-cyan-300 transition-all flex items-start gap-2"
                >
                  <Sparkles className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span className="line-clamp-2">{sp}</span>
                </button>
              ))}
            </div>
          </div>

          {/* User Prompt Box */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-400 uppercase">
              Describe Your Custom Document Requirements:
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., I need a driving license template for a gaming project..."
                className="flex-1 px-4 py-3 rounded-2xl bg-slate-900 border border-slate-700 text-xs font-medium focus:outline-none focus:border-cyan-400 text-white"
                onKeyDown={(e) => e.key === 'Enter' && handleAskAI()}
              />
              <button
                onClick={() => handleAskAI()}
                disabled={loading}
                className="px-5 py-3 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-extrabold text-xs flex items-center gap-2 shadow-lg shadow-blue-500/20 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Ask AI</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* AI Output Response Box */}
          {loading && (
            <div className="p-6 rounded-2xl bg-slate-900/60 border border-purple-500/30 text-center space-y-3">
              <Sparkles className="w-8 h-8 text-purple-400 animate-spin mx-auto" />
              <p className="text-xs font-bold text-purple-300">
                AI Engine is analyzing document template layer specs and font matches...
              </p>
            </div>
          )}

          {responseMarkdown && !loading && (
            <div className="p-6 rounded-2xl bg-slate-900/95 border border-cyan-500/40 space-y-3">
              <div className="flex items-center gap-2 text-cyan-400 text-xs font-bold border-b border-cyan-500/20 pb-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>AI Recommendation Result</span>
              </div>
              <div className="text-xs sm:text-sm leading-relaxed text-slate-200 whitespace-pre-line font-medium">
                {responseMarkdown}
              </div>
            </div>
          )}

        </div>

      </div>

    </div>
  );
};
